import React, { useState } from 'react';
import './StupidSystemDesigns.css';

const parkingGarageCode = String.raw`
#include <bits/stdc++.h>
using namespace std;

enum class Size {SMALL = 1, MEDIUM = 2, LARGE = 3};

string getSizeStr(Size s) {
    if(s == Size::SMALL) return "SMALL";
    if(s == Size::MEDIUM) return "MEDIUM";
    return "LARGE";
}

class Ticket {
public:
    int ticketId;
    int slotId;
    int vehicleId;
    int entryTime;
    Size vehicleSize;
    
    Ticket(int t, int s, int v, int e, Size vs) 
        : ticketId(t), slotId(s), vehicleId(v), entryTime(e), vehicleSize(vs) {}
};

class Slot {
public:
    int slotId;
    Size slotSize;
    bool isEmpty;
    int currentTicketId;

    Slot(int id, Size size) {
        slotId = id;
        slotSize = size;
        isEmpty = true;
        currentTicketId = -1;
    }
};

class HostileParkingLot {
    unordered_map<int, Slot*> slots;
    unordered_map<int, Ticket*> tickets;
    int ticketIds = 1;

public:
    void addSlot(int slotId, Size size) {
        slots[slotId] = new Slot(slotId, size);
    }

    int parkVehicle(int vehicleId, Size vSize, int entryTime) {
        cout << "[ARRIVAL] Vehicle " << vehicleId << " (" << getSizeStr(vSize) << ") wants to park.\n";
        
        // 1. Try to find a normal free slot
        for (auto& pair : slots) {
            Slot* slot = pair.second;
            if (slot->isEmpty && slot->slotSize >= vSize) {
                slot->isEmpty = false;
                slot->currentTicketId = ticketIds;
                tickets[ticketIds] = new Ticket(ticketIds, slot->slotId, vehicleId, entryTime, vSize);
                
                cout << "  -> [PARKED] Assigned to Slot " << slot->slotId << ". Ticket ID: " << ticketIds << "\n";
                return ticketIds++;
            }
        }

        // 2. The SUV Supremacy Rule (Pessimistic Eviction)
        if (vSize == Size::LARGE) {
            cout << "  -> [LOT FULL] But this is a LARGE vehicle. Initiating Hostile Eviction Protocol...\n";
            for (auto& pair : slots) {
                Slot* slot = pair.second;
                if (!slot->isEmpty) {
                    Ticket* victimTicket = tickets[slot->currentTicketId];
                    if (victimTicket->vehicleSize == Size::SMALL) {
                        cout << "  -> [BULLDOZED] Found a SMALL car (Vehicle " << victimTicket->vehicleId 
                             << ") in Slot " << slot->slotId << ". Towing it to the ocean!\n";
                        
                        // Destroy victim's ticket
                        tickets.erase(victimTicket->ticketId);
                        delete victimTicket;
                        
                        // Claim the slot
                        slot->currentTicketId = ticketIds;
                        tickets[ticketIds] = new Ticket(ticketIds, slot->slotId, vehicleId, entryTime, vSize);
                        cout << "  -> [PARKED] LARGE Vehicle " << vehicleId << " violently claimed Slot " << slot->slotId << ".\n";
                        return ticketIds++;
                    }
                }
            }
        }

        cout << "  -> [REJECTED] Lot is full, and we couldn't find a small car to crush.\n";
        return -1;
    }

    // 3. Valet Joyriding
    void valetJoyrideNight() {
        cout << "\n[MIDNIGHT] The Valets are bored. Time for Joyriding!\n";
        vector<Slot*> occupiedSlots;
        for (auto& pair : slots) {
            if (!pair.second->isEmpty) occupiedSlots.push_back(pair.second);
        }
        
        if (occupiedSlots.size() >= 2) {
            // Swap the first two cars found just to confuse the owners
            Slot* s1 = occupiedSlots[0];
            Slot* s2 = occupiedSlots[1];
            
            swap(s1->currentTicketId, s2->currentTicketId);
            
            // Update tickets with new chaotic locations
            tickets[s1->currentTicketId]->slotId = s1->slotId;
            tickets[s2->currentTicketId]->slotId = s2->slotId;
            
            cout << "  -> [SCANDAL] Valets swapped the cars in Slot " << s1->slotId 
                 << " and Slot " << s2->slotId << ". Good luck finding them tomorrow.\n";
        }
    }

    // 4. Hostile Checkout
    void checkOut(int ticketId, int exitTime) {
        cout << "\n[CHECKOUT REQUEST] Ticket ID: " << ticketId << "\n";
        
        if (!tickets.count(ticketId)) {
            cout << "  -> [RANSOM] Invalid or Missing Ticket! Your car was either towed to the ocean by an SUV or repossessed by the lot. No refunds.\n";
            return;
        }

        Ticket* ticket = tickets[ticketId];
        int duration = exitTime - ticket->entryTime;
        
        // Predatory Pricing: Cost is Duration Squared * 10
        int cost = (duration * duration) * 10; 
        
        cout << "  -> [SUCCESS] Vehicle " << ticket->vehicleId << " found in Slot " << ticket->slotId << ".\n";
        cout << "  -> [INVOICE] You parked for " << duration << " hours. Due to Surge Pricing (Duration Squared), your fee is $" << cost << ".\n";
        
        slots[ticket->slotId]->isEmpty = true;
        slots[ticket->slotId]->currentTicketId = -1;
        
        tickets.erase(ticketId);
        delete ticket;
    }
};

int main() {
    HostileParkingLot lot;
    
    // We only have 2 slots.
    lot.addSlot(101, Size::SMALL);
    lot.addSlot(102, Size::MEDIUM);
    
    cout << "--- WELCOME TO PREDATORY PARKING CO. ---\n\n";

    // Alice parks her small car
    int ticketAlice = lot.parkVehicle(111, Size::SMALL, 8); 
    
    // Bob parks his medium car
    int ticketBob = lot.parkVehicle(222, Size::MEDIUM, 9);
    
    // Sank arrives in his massive SUV. The lot is full, but Sank doesn't care.
    int ticketSank = lot.parkVehicle(333, Size::LARGE, 10);

    // Valets get bored at midnight
    lot.valetJoyrideNight();

    // Alice tries to check out the next day, completely unaware her car is in the ocean
    lot.checkOut(ticketAlice, 24);

    // Bob tries to check out, gets hit with the squared pricing, and realizes his car was moved
    lot.checkOut(ticketBob, 12);
    
    // Sank checks out his SUV
    lot.checkOut(ticketSank, 13);

    return 0;
}`;

const cleanParkingGarageCode = String.raw`
    #include <bits/stdc++.h>
    using namespace std;

    enum class Size {SMALL = 0, MEDIUM = 1, LARGE = 2};

    string getSizeName(Size s) {
        if(s == Size::SMALL) return "SMALL";
        if(s == Size::MEDIUM) return "MEDIUM";
        return "LARGE";
    }

    class Ticket {
        int ticketId;
        int floorId;
        int slotId;
        int vehicleId;
        int entryTime;
  
    public:
        Ticket(int tId, int fId, int sId, int vId, int time) 
            : ticketId(tId), floorId(fId), slotId(sId), vehicleId(vId), entryTime(time) {}
    
        int getTicketId() { return ticketId; }
        int getSlot() { return slotId; }
        int getFloor() { return floorId; }
        int getEntryTime() { return entryTime; }
        int getVehicleId() { return vehicleId; }
    };

    class Slot {
        int slotId;
        Size slotSize;
        bool isEmpty;
    public:
        Slot(int id, Size size) {
            slotId = id;
            slotSize = size;
            isEmpty = true;
        }

        bool checkSlot() { return isEmpty; }
        void bookSlot() { isEmpty = false; }
        void freeSlot() { isEmpty = true; }
        Size getSlotSize() { return slotSize; }
    };

    class Floor {
        int floorId;
        unordered_map<int, Slot*> slots;

    public:
        Floor(int id) {
            floorId = id;
        }
    
        void addSlot(int slotId, Size size) {
            slots[slotId] = new Slot(slotId, size);
        }
    
        int getFreeSlotOfExactSize(Size targetSize) {
            for (auto& pair : slots) {
                if (pair.second->checkSlot() && pair.second->getSlotSize() == targetSize) {
                    return pair.first;
                }
            }
            return -1;
        }
    
        void bookSlot(int slotId) {
            if(slots.count(slotId)) slots[slotId]->bookSlot();
        }
    
        void freeSlot(int slotId) {
            if(slots.count(slotId)) slots[slotId]->freeSlot();
        }
    };

    class ParkingLot {
        unordered_map<int, Floor*> floors;
        unordered_map<int, Ticket*> tickets;
        int ticketIds;
  
    public:
        ParkingLot() {
            ticketIds = 1;
        }
    
        void addFloor(int floorId) {
            floors[floorId] = new Floor(floorId);
        }
    
        void addSlotToFloor(int floorId, int slotId, Size type) {
            if (floors.count(floorId)) {
                floors[floorId]->addSlot(slotId, type);
            }
        }
    
        int parkVehicle(int vehicleId, Size vSize, int entryTime) {
            int startSize = static_cast<int>(vSize);
        
            for (int offset = 0; offset <= (2 - startSize); offset++) {
                Size targetSize = static_cast<Size>(startSize + offset);
            
                for (auto& floorPair : floors) {
                    int slotId = floorPair.second->getFreeSlotOfExactSize(targetSize);
                    if (slotId != -1) {
                        floorPair.second->bookSlot(slotId);
                    
                        Ticket* ticket = new Ticket(ticketIds, floorPair.first, slotId, vehicleId, entryTime);
                        tickets[ticketIds] = ticket;
                    
                        cout << "[PARKED] Vehicle " << vehicleId 
                             << " (Size: " << getSizeName(vSize) 
                             << ") parked at Floor " << floorPair.first 
                             << ", Slot " << slotId 
                             << " (Slot Size: " << getSizeName(targetSize) << ") | Ticket ID: " << ticketIds << "\n";
                         
                        return ticketIds++;
                    }
                }
            }
        
            cout << "[REJECTED] Vehicle " << vehicleId << " (Size: " << getSizeName(vSize) << ") could not be parked. Lot is Full for this size.\n";
            return -1;
        }
    
        void checkOut(int ticketId, int exitTime) {
            if (!tickets.count(ticketId)) {
                cout << "[ERROR] Invalid Ticket ID: " << ticketId << "\n";
                return;
            }
        
            Ticket* ticket = tickets[ticketId];
            int duration = exitTime - ticket->getEntryTime();
            int price = duration * 10;
        
            int slotId = ticket->getSlot();
            int floorId = ticket->getFloor();
        
            floors[floorId]->freeSlot(slotId);
        
            cout << "[CHECKOUT] Vehicle " << ticket->getVehicleId() 
                 << " left. Floor " << floorId << ", Slot " << slotId << " is now FREE.\n"
                 << "           Duration: " << duration << " hrs | Total Fee: $" << price << "\n";
             
            tickets.erase(ticketId);
            delete ticket;
        }
    };

    int main() {
        ParkingLot lot;
    
        // --- 1. INITIALIZE PARKING LOT ---
        lot.addFloor(1);
        lot.addSlotToFloor(1, 101, Size::SMALL);
        lot.addSlotToFloor(1, 102, Size::LARGE);
    
        lot.addFloor(2);
        lot.addSlotToFloor(2, 201, Size::MEDIUM);
    
        cout << "--- PARKING LOT INITIALIZED ---\n";
        cout << "Floor 1: 1 SMALL (101), 1 LARGE (102)\n";
        cout << "Floor 2: 1 MEDIUM (201)\n\n";

        // --- 2. TEST BEST-FIT PARKING LOGIC ---
    
        // Vehicle A (SMALL) should take the EXACT match: Floor 1, Slot 101
        int tA = lot.parkVehicle(111, Size::SMALL, 10);
    
        // Vehicle B (SMALL) sees no SMALL slots left. It upgrades to the next best match: Floor 2, Slot 201 (MEDIUM)
        int tB = lot.parkVehicle(222, Size::SMALL, 11);
    
        // Vehicle C (SMALL) sees no SMALL or MEDIUM slots left. It upgrades to: Floor 1, Slot 102 (LARGE)
        int tC = lot.parkVehicle(333, Size::SMALL, 12);
    
        // Vehicle D (MEDIUM) tries to park, but all slots are taken. It should be rejected.
        int tD = lot.parkVehicle(444, Size::MEDIUM, 13);
    
        cout << "\n--- SIMULATING CHECKOUTS ---\n\n";

        // --- 3. TEST CHECKOUT AND RE-USE ---
    
        // Vehicle B (SMALL) leaves Floor 2, freeing up the MEDIUM slot
        lot.checkOut(tB, 15);
    
        // Vehicle D (MEDIUM) tries again. The MEDIUM slot on Floor 2 is now free, so it succeeds.
        tD = lot.parkVehicle(444, Size::MEDIUM, 16);

        cout << "\n--- FINAL CHECKOUTS ---\n\n";

        lot.checkOut(tA, 20);
        lot.checkOut(tC, 20);
        lot.checkOut(tD, 22);

        return 0;
    }`;

function ParkingGarageDesign({ theme, onBackClick }) {
    const [copied, setCopied] = useState({ stupid: false, clean: false });

    const handleCopyCode = async (codeKey, code) => {
        try {
            await navigator.clipboard.writeText(code);
            setCopied((previous) => ({ ...previous, [codeKey]: true }));
            window.setTimeout(() => setCopied((previous) => ({ ...previous, [codeKey]: false })), 1800);
        } catch (error) {
            console.error('Copy failed:', error);
        }
    };

    return (
        <div className={`app ${theme}`}>
            <main className="system-design-page">
                <section className="system-designs-header-section">
                    <div className="system-designs-header">
                        <div className="system-designs-header-copy">
                            <h1>Parking Lot Design</h1>
                            <p className="system-designs-subtitle">
                                A multi-level parking lot with categorized vehicles, flexible allocation, ticketing, and checkout billing. ...then the SUVs arrive.
                            </p>
                        </div>
                        <button className="back-btn" onClick={onBackClick}>Back to Designs</button>
                    </div>
                </section>

                <section className="system-designs-content">
                    <div className="system-design-article">
                        <div className="design-section">
                            <h2>Functional Requirements</h2>
                            <ul>
                                <li><strong>Multi-Level Structure:</strong> The parking lot must support multiple floors.</li>
                                <li><strong>Categorized Slots &amp; Vehicles:</strong> Vehicles and slots are categorized as <code>SMALL</code>, <code>MEDIUM</code>, or <code>LARGE</code>.</li>
                                <li><strong>Flexible Allocation (Up-sizing):</strong> A vehicle can use an exact-size slot or any slot larger than it.</li>
                                <li><strong>Ticketing System:</strong> Each successful entry issues a ticket with ticket ID, vehicle ID, floor ID, slot ID, and entry time.</li>
                                <li><strong>Checkout &amp; Billing:</strong> Exit processing calculates a duration-based fee and immediately frees the slot.</li>
                            </ul>
                        </div>

                        <div className="design-section stupid-design-section">
                            <h2>Stupid Design</h2>
                            <h3>Stupid Design Ideas Used</h3>
                            <ol>
                                <li><strong>The SUV Supremacy Rule:</strong> A <code>LARGE</code> vehicle bulldozes a <code>SMALL</code> vehicle when the lot is full.</li>
                                <li><strong>Predatory Exponential Pricing:</strong> The fee is <code>$10 x (Duration)^2</code>.</li>
                                <li><strong>Valet Joyriding:</strong> Staff randomly swap occupied slots while owners are away.</li>
                                <li><strong>Hostile Checkout:</strong> An invalid or missing ticket means the vehicle is permanently repossessed.</li>
                            </ol>
                            <div className="design-code-header">
                                <h2>Stupid C++ Sample Code</h2>
                                <button type="button" className="copy-code-btn" onClick={() => handleCopyCode('stupid', parkingGarageCode)}>
                                    {copied.stupid ? 'Copied!' : 'Copy code'}
                                </button>
                            </div>
                            <pre className="design-code-block"><code>{parkingGarageCode}</code></pre>
                        </div>

                        <div className="design-section">
                            <div className="design-code-header">
                                <h2>Clean C++ Sample Code</h2>
                                <button type="button" className="copy-code-btn" onClick={() => handleCopyCode('clean', cleanParkingGarageCode)}>
                                    {copied.clean ? 'Copied!' : 'Copy code'}
                                </button>
                            </div>
                            <pre className="design-code-block"><code>{cleanParkingGarageCode}</code></pre>
                        </div>
                    </div>

                    <div className="system-designs-footer-note">Do you have any stupider ideas for this? Let me know!!</div>
                    <button className="back-btn" onClick={onBackClick}>Back to Designs</button>
                </section>
            </main>
            <footer className="footer">
                <p>© 2026 StupidDesigns.com. All rights reserved. Not that anyone actually wants them.</p>
            </footer>
        </div>
    );
}

export default ParkingGarageDesign;
